import { connect, ResolveThunks } from "react-redux";
import { rootSelectors, RootState } from "state/rootReducer";
import SpotifyPlayer, {
  CallbackState,
  ERROR_TYPE,
  SpotifyTrack,
  STATUS,
} from "react-spotify-web-playback";
import {
  actions as userActions,
  SongFeedbackAndSeed,
} from "state/slices/userSlice";
import { actions as songActions } from "state/slices/songsSlice";
// Spotify's guidelines require the green (or white) mark on dark grounds —
// the black mark is for light backgrounds only.
import spotifyLogo from "layout/SpotifyBar/assets/spotify-logo.png";
import { useCallback, useEffect, useRef, useState } from "react";
import SongActionBar from "components/SongActionBar/SongActionBar";
import { useWakeLock } from "shared/wakeLock";

/** New players to start in a row after authentication errors before giving up. */
const MAX_AUTH_RECOVERIES = 2;

interface StateProps {
  spotifyUser: SpotifyUser | null;
  token: string | null;
  expirationTime: number | null;
  playlistUris: string[] | null;
  isPlaying: boolean;
  playRequest: number;
  offset: number | undefined;
  currentTrackId: string | null;
  songFeedbackByGid: Record<string, SongFeedbackAndSeed> | null;
  playerTrack: SpotifyTrack | null;
}

interface DispatchProps {
  onSetPlayingStatus: typeof songActions.setPlayingStatus;
  onAddFeedback: typeof userActions.addFeedback;
  onRefreshAccessToken: typeof userActions.refreshAccessToken;
}

export type SpotifyBarProps = StateProps & ResolveThunks<DispatchProps>;

function getTrackUri(song: Song): string {
  return `spotify:track:${song.spotify_id}`;
}

/**
 * The player bar.
 *
 * The player mounts at login, before anything is queued, and stays hidden
 * until something is. It used to mount on the first press of play. Loading the
 * SDK and registering a device took longer than that click, so the press was
 * lost and a second one was needed on the player's own controls.
 */
export function SpotifyBar(props: SpotifyBarProps): JSX.Element {
  /** Whether the player has had an active playback session. */
  const [loaded, setLoaded] = useState(false);
  /** Whether this page's Spotify device has registered and can take playback. */
  const [ready, setReady] = useState(false);
  /** Changing this starts a new player, after an auth error kills the old one. */
  const [playerKey, setPlayerKey] = useState(0);
  const authRecoveries = useRef(0);
  // The store is restored on page load, so a play request made in an earlier
  // visit must not autoplay. Only a request made since this page loaded counts.
  const initialPlayRequest = useRef(props.playRequest);
  const hasPlayRequest = props.playRequest !== initialPlayRequest.current;
  // Not gated on the token's expiry. Refreshing replaces the token in place, and
  // unmounting the player to wait for a new one stopped playback mid-song.
  const canMountPlayer = !!props.token;
  const hasQueue = props.playlistUris !== null;

  // Spotify's SDK takes this callback once, when the player starts, and calls it
  // for a token when it connects and each time its token runs out. Reading props
  // through a ref gives it the current token, not the one from start-up. The
  // library used to capture the start-up token, which is where the 401s came from.
  const latestProps = useRef(props);
  latestProps.current = props;

  const getOAuthToken = useCallback(
    async (callback: (token: string) => void) => {
      const { token, expirationTime, onRefreshAccessToken } =
        latestProps.current;

      if (token && (expirationTime === null || Date.now() < expirationTime)) {
        callback(token);

        return;
      }

      const refreshed = await onRefreshAccessToken();
      const next = refreshed ?? latestProps.current.token;

      if (next) {
        callback(next);
      }
    },
    []
  );

  useWakeLock(hasQueue && props.isPlaying);

  useEffect(() => {
    if (!canMountPlayer) {
      setReady(false);
    }
  }, [canMountPlayer]);

  function recoverFromAuthError(): void {
    if (authRecoveries.current >= MAX_AUTH_RECOVERIES) {
      console.error("Spotify player keeps rejecting its token; giving up.");

      return;
    }

    authRecoveries.current += 1;

    void props.onRefreshAccessToken().then((token) => {
      if (token) {
        // The new player picks up at the current track, via `offset`.
        setLoaded(false);
        setPlayerKey((key) => key + 1);
      }
    });
  }

  function updatePlayingState(state: CallbackState): void {
    setReady(state.status === STATUS.READY);

    // The library disconnects the player on an authentication error and never
    // reconnects it.
    if (
      state.status === STATUS.ERROR &&
      state.errorType === ERROR_TYPE.AUTHENTICATION
    ) {
      recoverFromAuthError();
    }

    if (!state.isInitializing && state.isActive) {
      authRecoveries.current = 0;

      props.onSetPlayingStatus({
        isPlaying: state.isPlaying,
        playingNow: state.track,
      });

      setLoaded(true);
    }

    if (
      props.currentTrackId &&
      state.track.id &&
      props.currentTrackId !== state.track.id
    ) {
      console.log(
        "#### DEBUG TRACK: CURRENT " +
          props.currentTrackId +
          " FROM CALLBACK: " +
          state.track.id
      );
      //window.location.reload();
    }
  }

  function render(): JSX.Element {
    // Nothing to play and nobody signed in: render nothing rather than an
    // empty bar taking up vertical space.
    if (props.spotifyUser === null) {
      return <></>;
    }

    // Hidden, not display:none, while nothing is queued. The player measures
    // its position when it mounts to decide which way its device menu opens.
    // A collapsed bar still sits at the bottom, so the menu keeps opening
    // upward.
    return (
      <div
        className={
          hasQueue
            ? "flex-none w-full bg-surface border-t border-rule overflow-x-hidden"
            : "flex-none w-full h-0 overflow-hidden invisible"
        }
      >
        {showSpotifyInfo()}
      </div>
    );
  }

  function showSpotifyInfo(): JSX.Element {
    console.log("Spotify player playing: " + props.isPlaying);
    return (
      <div className="px-3 sm:px-4 py-2 flex flex-col gap-2 text-sm">
        {canMountPlayer && (
          <div>
            <SpotifyPlayer
              key={playerKey}
              token={props.token!}
              getOAuthToken={getOAuthToken}
              uris={props.playlistUris ?? []}
              styles={{
                bgColor: "#17141f",
                color: "#f3f0fa",
                trackNameColor: "#f3f0fa",
                trackArtistColor: "#a79fbc",
                sliderColor: "#b14eff",
                sliderHandleColor: "#f3f0fa",
                sliderTrackColor: "#282232",
                loaderColor: "#b14eff",
                errorColor: "#e5484d",
              }}
              offset={!loaded ? props.offset : undefined}
              callback={(state) => updatePlayingState(state)}
              // Wait for this page's device. Before it registers, the library
              // sends play with no device id, and Spotify starts whichever
              // device is active, such as the user's phone.
              play={
                ready && (loaded || hasPlayRequest) ? props.isPlaying : false
              }
              showSaveIcon={!!props.token}
            />
          </div>
        )}
        <div className="flex flex-row flex-wrap items-center gap-2">
          {props.currentTrackId && (
            <SongActionBar
              addBorder={true}
              currentFeedback={props.songFeedbackByGid?.[props.currentTrackId]}
              hasFeedback={!!props.songFeedbackByGid?.[props.currentTrackId]}
              trackId={props.currentTrackId}
              onAddFeedback={props.onAddFeedback}
            />
          )}
          {props.currentTrackId && props.playerTrack?.id ? (
            // Just the logo on phones, so it fits on the feedback row instead
            // of wrapping to a line of its own. The label names the link at
            // every width.
            <a
              className="flex-none flex items-center gap-2 p-1 sm:pl-2 sm:pr-3 bordered ml-auto"
              href={`https://open.spotify.com/track/${props.playerTrack.id}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Open Spotify"
              title="Open Spotify"
            >
              <img
                src={spotifyLogo}
                alt=""
                width="24"
                height="24"
                className="w-6 h-6"
              />
              <span className="hidden sm:inline">Open Spotify</span>
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return render();
}

function mapState(state: RootState): StateProps {
  return {
    spotifyUser: state.user.currentUser,
    token: state.user.accessToken,
    expirationTime: state.user.expirationTime,
    playlistUris:
      rootSelectors.songs.getPlaylist(state)?.map((s) => getTrackUri(s)) ||
      null,
    isPlaying: state.songs.isPlaying,
    playRequest: state.songs.playRequest ?? 0,
    offset: state.songs.offset ? state.songs.offset : undefined,
    currentTrackId: state.songs.currentTrackId,
    songFeedbackByGid: rootSelectors.user.getSongFeedbackByGid(state),
    playerTrack: state.songs.playerTrack,
  };
}

const mapDispatch: DispatchProps = {
  onSetPlayingStatus: songActions.setPlayingStatus,
  onAddFeedback: userActions.addFeedback,
  onRefreshAccessToken: userActions.refreshAccessToken,
};

export default connect(mapState, mapDispatch)(SpotifyBar);
